import { collection, doc, getDoc, setDoc, updateDoc, getDocs, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface FeeCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  fees: Fee[];
}

export interface Fee {
  id: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed' | 'tiered';
  value: number;
  minAmount?: number;
  maxAmount?: number;
  currency: string;
  isActive: boolean;
  applicableFeatures: string[];
  lastUpdated: string;
  updatedBy: string;
}

export interface TieredFee extends Fee {
  type: 'tiered';
  tiers: {
    minAmount: number;
    maxAmount: number;
    rate: number;
  }[];
}

export interface FeeAuditContext {
  actorId?: string;
  actorEmail?: string;
  actorName?: string;
}

export class FeeManagementService {
  private static readonly FEES_COLLECTION = 'platformFees';
  private static readonly FEE_CATEGORIES_COLLECTION = 'feeCategories';
  private static readonly FEE_AUDIT_COLLECTION = 'feeAuditLogs';

  private static async writeFeeAuditLog(params: {
    action: 'add' | 'update' | 'delete';
    categoryId: string;
    categoryName?: string;
    feeId: string;
    previousValue: Fee | null;
    newValue: Fee | null;
    context?: FeeAuditContext;
  }): Promise<void> {
    try {
      await addDoc(collection(db, this.FEE_AUDIT_COLLECTION), {
        action: params.action,
        categoryId: params.categoryId,
        categoryName: params.categoryName || null,
        feeId: params.feeId,
        previousValue: params.previousValue,
        newValue: params.newValue,
        actorId: params.context?.actorId || 'unknown',
        actorEmail: params.context?.actorEmail || null,
        actorName: params.context?.actorName || null,
        createdAt: new Date().toISOString(),
      });
    } catch (auditError) {
      console.error('Error writing fee audit log:', auditError);
    }
  }

  // Default fee structure for InTouch platform
  static getDefaultFeeStructure(): FeeCategory[] {
    return [
      {
        id: 'marketplace',
        name: 'Marketplace & E-commerce',
        description: 'Fees for marketplace transactions and seller activities',
        icon: '🛍️',
        fees: [
          {
            id: 'marketplace_commission',
            name: 'Marketplace Commission',
            description: 'Commission on product sales',
            type: 'percentage',
            value: 5.0,
            currency: 'USD',
            isActive: true,
            applicableFeatures: ['product_sale', 'digital_product_sale'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'seller_listing_fee',
            name: 'Product Listing Fee',
            description: 'Fee for listing products on marketplace',
            type: 'fixed',
            value: 0.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['product_listing'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'seller_verification_fee',
            name: 'Seller Verification',
            description: 'One-time fee for seller account verification',
            type: 'fixed',
            value: 9.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['seller_verification'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'social_content',
        name: 'Social & Content',
        description: 'Fees for premium social features and content',
        icon: '📱',
        fees: [
          {
            id: 'premium_post_fee',
            name: 'Premium Post Boost',
            description: 'Fee to boost post visibility',
            type: 'fixed',
            value: 1.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['post_boost', 'promoted_post'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'group_creation_fee',
            name: 'Group Creation',
            description: 'Fee for creating new groups',
            type: 'fixed',
            value: 4.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['group_creation'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'event_hosting_fee',
            name: 'Event Hosting',
            description: 'Fee for hosting events',
            type: 'percentage',
            value: 3.0,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['event_creation', 'paid_event'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'communication',
        name: 'Communication',
        description: 'Fees for premium messaging and communication features',
        icon: '💬',
        fees: [
          {
            id: 'premium_messaging_fee',
            name: 'Premium Messaging',
            description: 'Monthly fee for premium messaging features',
            type: 'fixed',
            value: 2.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['premium_messaging', 'unlimited_messages'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'video_call_fee',
            name: 'Video Call Minutes',
            description: 'Fee per minute for video calls',
            type: 'fixed',
            value: 0.05,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['video_call', 'group_video_call'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'file_sharing_fee',
            name: 'Large File Sharing',
            description: 'Fee for sharing files over 100MB',
            type: 'fixed',
            value: 0.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['large_file_sharing'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'financial_services',
        name: 'Financial Services',
        description: 'Fees for crypto mining, staking, and exchange services',
        icon: '💰',
        fees: [
          {
            id: 'mining_fee',
            name: 'Mining Pool Fee',
            description: 'Fee for participating in mining pools',
            type: 'percentage',
            value: 2.0,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['crypto_mining', 'mining_pool'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'staking_fee',
            name: 'Staking Service Fee',
            description: 'Fee for staking services',
            type: 'percentage',
            value: 1.5,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['crypto_staking', 'staking_rewards'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'exchange_fee',
            name: 'Exchange Transaction Fee',
            description: 'Fee for crypto exchange transactions',
            type: 'percentage',
            value: 0.5,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['crypto_exchange', 'token_swap'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'wallet_transaction_fee',
            name: 'Wallet Transaction Fee',
            description: 'Fee for wallet transactions',
            type: 'fixed',
            value: 0.25,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['wallet_transfer', 'payment_processing'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'learning_education',
        name: 'Learning & Education',
        description: 'Fees for educational content and courses',
        icon: '📚',
        fees: [
          {
            id: 'course_creation_fee',
            name: 'Course Creation Fee',
            description: 'Fee for creating and publishing courses',
            type: 'fixed',
            value: 19.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['course_creation', 'course_publishing'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'certification_fee',
            name: 'Certification Fee',
            description: 'Fee for issuing certificates',
            type: 'fixed',
            value: 9.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['certification_issuance'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'premium_content_fee',
            name: 'Premium Content Access',
            description: 'Monthly fee for premium learning content',
            type: 'fixed',
            value: 14.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['premium_learning_content'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'entertainment',
        name: 'Entertainment',
        description: 'Fees for gaming, music, and entertainment features',
        icon: '🎮',
        fees: [
          {
            id: 'premium_games_fee',
            name: 'Premium Games Access',
            description: 'Monthly fee for premium games',
            type: 'fixed',
            value: 9.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['premium_games', 'game_tournaments'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'music_streaming_fee',
            name: 'Music Streaming',
            description: 'Monthly fee for ad-free music streaming',
            type: 'fixed',
            value: 7.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['music_streaming', 'premium_music'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'exclusive_content_fee',
            name: 'Exclusive Content',
            description: 'Fee for accessing exclusive entertainment content',
            type: 'fixed',
            value: 4.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['exclusive_content', 'premium_entertainment'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'professional_services',
        name: 'Professional Services',
        description: 'Fees for freelance and professional services',
        icon: '💼',
        fees: [
          {
            id: 'freelance_commission',
            name: 'Freelance Commission',
            description: 'Commission on freelance project payments',
            type: 'percentage',
            value: 8.0,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['freelance_projects', 'professional_services'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'job_posting_fee',
            name: 'Job Posting Fee',
            description: 'Fee for posting job listings',
            type: 'fixed',
            value: 29.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['job_posting', 'recruitment'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'professional_verification_fee',
            name: 'Professional Verification',
            description: 'Fee for professional profile verification',
            type: 'fixed',
            value: 24.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['professional_verification'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'location_travel',
        name: 'Location & Travel',
        description: 'Fees for transportation and location-based services',
        icon: '🚗',
        fees: [
          {
            id: 'transportation_booking_fee',
            name: 'Transportation Booking Fee',
            description: 'Fee for booking transportation services',
            type: 'percentage',
            value: 3.5,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['transportation_booking', 'travel_booking'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'premium_location_fee',
            name: 'Premium Location Features',
            description: 'Fee for premium location-based features',
            type: 'fixed',
            value: 2.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['premium_location', 'location_analytics'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      },
      {
        id: 'specialized_services',
        name: 'Specialized Services',
        description: 'Fees for specialized platform services',
        icon: '⚙️',
        fees: [
          {
            id: 'kyc_verification_fee',
            name: 'KYC Verification',
            description: 'Fee for Know Your Customer verification',
            type: 'fixed',
            value: 14.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['kyc_verification', 'identity_verification'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'charity_donation_fee',
            name: 'Charity Donation Processing',
            description: 'Processing fee for charity donations',
            type: 'percentage',
            value: 2.5,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['charity_donations'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          },
          {
            id: 'premium_support_fee',
            name: 'Premium Support',
            description: 'Monthly fee for premium customer support',
            type: 'fixed',
            value: 19.99,
            currency: 'USD',
            isActive: false,
            applicableFeatures: ['premium_support', 'priority_support'],
            lastUpdated: new Date().toISOString(),
            updatedBy: 'system'
          }
        ]
      }
    ];
  }

  static async initializeFeeStructure(): Promise<void> {
    try {
      const defaultCategories = this.getDefaultFeeStructure();
      
      for (const category of defaultCategories) {
        const categoryRef = doc(db, this.FEE_CATEGORIES_COLLECTION, category.id);
        await setDoc(categoryRef, category);
      }

      console.log('Fee structure initialized successfully');
    } catch (error) {
      console.error('Error initializing fee structure:', error);
      throw error;
    }
  }

  static async getFeeCategories(): Promise<FeeCategory[]> {
    try {
      const snapshot = await getDocs(collection(db, this.FEE_CATEGORIES_COLLECTION));
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FeeCategory));
    } catch (error) {
      console.error('Error fetching fee categories:', error);
      throw error;
    }
  }

  static async updateFee(
    categoryId: string,
    feeId: string,
    updates: Partial<Fee>,
    context?: FeeAuditContext
  ): Promise<void> {
    try {
      const categoryRef = doc(db, this.FEE_CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (!categoryDoc.exists()) {
        throw new Error('Fee category not found');
      }

      const categoryData = categoryDoc.data() as FeeCategory;
      const feeIndex = categoryData.fees.findIndex(fee => fee.id === feeId);
      
      if (feeIndex === -1) {
        throw new Error('Fee not found');
      }

      const previousFee = { ...categoryData.fees[feeIndex] };

      categoryData.fees[feeIndex] = {
        ...categoryData.fees[feeIndex],
        ...updates,
        lastUpdated: new Date().toISOString()
      };

      const updatedFee = categoryData.fees[feeIndex];

      await updateDoc(categoryRef, { fees: categoryData.fees });

      await this.writeFeeAuditLog({
        action: 'update',
        categoryId,
        categoryName: categoryData.name,
        feeId,
        previousValue: previousFee,
        newValue: updatedFee,
        context,
      });
    } catch (error) {
      console.error('Error updating fee:', error);
      throw error;
    }
  }

  static async addFee(categoryId: string, fee: Fee, context?: FeeAuditContext): Promise<void> {
    try {
      const categoryRef = doc(db, this.FEE_CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (!categoryDoc.exists()) {
        throw new Error('Fee category not found');
      }

      const categoryData = categoryDoc.data() as FeeCategory;
      categoryData.fees.push(fee);

      await updateDoc(categoryRef, { fees: categoryData.fees });

      await this.writeFeeAuditLog({
        action: 'add',
        categoryId,
        categoryName: categoryData.name,
        feeId: fee.id,
        previousValue: null,
        newValue: fee,
        context,
      });
    } catch (error) {
      console.error('Error adding fee:', error);
      throw error;
    }
  }

  static async deleteFee(categoryId: string, feeId: string, context?: FeeAuditContext): Promise<void> {
    try {
      const categoryRef = doc(db, this.FEE_CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (!categoryDoc.exists()) {
        throw new Error('Fee category not found');
      }

      const categoryData = categoryDoc.data() as FeeCategory;
      const deletedFee = categoryData.fees.find(fee => fee.id === feeId) || null;
      categoryData.fees = categoryData.fees.filter(fee => fee.id !== feeId);

      await updateDoc(categoryRef, { fees: categoryData.fees });

      await this.writeFeeAuditLog({
        action: 'delete',
        categoryId,
        categoryName: categoryData.name,
        feeId,
        previousValue: deletedFee,
        newValue: null,
        context,
      });
    } catch (error) {
      console.error('Error deleting fee:', error);
      throw error;
    }
  }

  static calculateFee(amount: number, fee: Fee): number {
    switch (fee.type) {
      case 'percentage':
        return (amount * fee.value) / 100;
      case 'fixed':
        return fee.value;
      case 'tiered':
        const tieredFee = fee as TieredFee;
        const tier = tieredFee.tiers.find(t => amount >= t.minAmount && amount <= t.maxAmount);
        return tier ? (amount * tier.rate) / 100 : 0;
      default:
        return 0;
    }
  }

  static async getApplicableFees(feature: string): Promise<Fee[]> {
    try {
      const categories = await this.getFeeCategories();
      const applicableFees: Fee[] = [];

      for (const category of categories) {
        for (const fee of category.fees) {
          if (fee.isActive && fee.applicableFeatures.includes(feature)) {
            applicableFees.push(fee);
          }
        }
      }

      return applicableFees;
    } catch (error) {
      console.error('Error getting applicable fees:', error);
      throw error;
    }
  }
}